import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Group,
  Modal,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { IconSearch, IconSparkles } from "@tabler/icons-react";
import NewBadge from "../../common/NewBadge";
import Pill from "../../common/Pill";
import { useResponsive } from "../../../context/ResponsiveContext";
import { SalonReport } from "../../../types/reports";
import {
  CatalogEntry,
  countByType,
  newEntriesNotInList,
  ReportCatalog,
} from "./reportCatalog";
import { reportIcon } from "./reportIcons";
import styles from "./AddReportModal.module.css";

/** Left-nav key for the synthesised "What's new" entry (not a category). */
export const WHATS_NEW_NAV = "__whats_new__";

export interface AddReportModalProps {
  opened: boolean;
  onClose: () => void;
  catalog: ReportCatalog;
  /** The user's saved reports (for "In your list ×N" / "Add again"). */
  reports: SalonReport[];
  onAdd: (entry: CatalogEntry) => void;
  /** Report type currently being added (shows a spinner on its button). */
  addingType?: string | null;
}

interface Section {
  key: string;
  label: string;
  entries: CatalogEntry[];
}

const matches = (entry: CatalogEntry, q: string, categoryLabel: string) =>
  `${entry.name} ${entry.description} ${entry.subGroup} ${categoryLabel}`
    .toLowerCase()
    .includes(q);

function bySubGroup(entries: CatalogEntry[]): Section[] {
  const groups = new Map<string, CatalogEntry[]>();
  for (const e of entries) {
    if (!groups.has(e.subGroup)) groups.set(e.subGroup, []);
    groups.get(e.subGroup)!.push(e);
  }
  return Array.from(groups, ([label, list]) => ({
    key: label,
    label,
    entries: list,
  }));
}

/**
 * The "Add a report" catalog. Left: "What's new" (only when the catalog has
 * NEW reports), then every category including Legacy, each with a count.
 * Right: the selected category grouped by sub-group (What's new and search
 * results are grouped by category). Each row shows the title, a NEW badge,
 * "In your list" / "In your list ×N", the description, and Add / Add again.
 * Search runs across every category. Full screen on phones.
 */
export default function AddReportModal({
  opened,
  onClose,
  catalog,
  reports,
  onAdd,
  addingType = null,
}: AddReportModalProps) {
  const { isMobile } = useResponsive();
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen={isMobile}
      size="xl"
      radius="lg"
      title={
        <Box>
          <Title order={2} fz="lg" fw={600}>
            Add a report
          </Title>
          <Text className={styles.subtitle}>
            Add a report more than once to keep separate views, like different
            date ranges or staff.
          </Text>
        </Box>
      }
    >
      {/* The Modal unmounts its content while closed, so the search and the
          selected category start fresh on every open. */}
      <CatalogBrowser
        catalog={catalog}
        reports={reports}
        onAdd={onAdd}
        addingType={addingType}
      />
    </Modal>
  );
}

interface CatalogBrowserProps {
  catalog: ReportCatalog;
  reports: SalonReport[];
  onAdd: (entry: CatalogEntry) => void;
  addingType: string | null;
}

function CatalogBrowser({
  catalog,
  reports,
  onAdd,
  addingType,
}: CatalogBrowserProps) {
  const [query, setQuery] = useState("");
  const hasNew = catalog.entries.some((e) => e.isNew);
  // Land on What's new while there are NEW reports the user hasn't added,
  // otherwise on the first category.
  const [nav, setNav] = useState<string>(() =>
    newEntriesNotInList(catalog, reports).length > 0
      ? WHATS_NEW_NAV
      : (catalog.categories[0]?.key ?? WHATS_NEW_NAV),
  );

  const counts = useMemo(() => countByType(reports), [reports]);
  const categoryLabel = useMemo(
    () => new Map(catalog.categories.map((c) => [c.key, c.label])),
    [catalog.categories],
  );

  const q = query.trim().toLowerCase();
  const matching = useMemo(
    () =>
      q
        ? catalog.entries.filter((e) =>
            matches(e, q, categoryLabel.get(e.category) ?? ""),
          )
        : null,
    [catalog.entries, q, categoryLabel],
  );

  const activeNav =
    nav === WHATS_NEW_NAV && !hasNew ? catalog.categories[0]?.key : nav;

  const sections: Section[] = useMemo(() => {
    const byCategory = (list: CatalogEntry[]) =>
      catalog.categories
        .map((c) => ({
          key: c.key,
          label: c.label,
          entries: list.filter((e) => e.category === c.key),
        }))
        .filter((s) => s.entries.length > 0);
    if (matching) return byCategory(matching);
    if (activeNav === WHATS_NEW_NAV) {
      return byCategory(catalog.entries.filter((e) => e.isNew));
    }
    return bySubGroup(catalog.entries.filter((e) => e.category === activeNav));
  }, [matching, activeNav, catalog]);

  const pool = matching ?? catalog.entries;
  const navItems = [
    ...(hasNew
      ? [
          {
            key: WHATS_NEW_NAV,
            label: "What’s new",
            Icon: IconSparkles,
            count: pool.filter((e) => e.isNew).length,
            highlight: true,
          },
        ]
      : []),
    ...catalog.categories.map((c) => ({
      key: c.key,
      label: c.label,
      Icon: reportIcon(c.icon),
      count: pool.filter((e) => e.category === c.key).length,
      highlight: false,
    })),
  ];

  return (
    <>
      <TextInput
        mb="sm"
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        placeholder="Search reports"
        aria-label="Search reports"
        leftSection={<IconSearch size={15} aria-hidden />}
      />
      <Box className={styles.panes}>
        <Box
          component="nav"
          aria-label="Report categories"
          className={styles.nav}
        >
          {navItems.map(({ key, label, Icon, count, highlight }) => {
            const current = !matching && key === activeNav;
            return (
              <UnstyledButton
                key={key}
                type="button"
                className={styles.navItem}
                aria-current={current ? "true" : undefined}
                data-empty={!!matching && count === 0}
                onClick={() => {
                  setQuery("");
                  setNav(key);
                }}
              >
                <Box component="span" className={styles.navIcon}>
                  <Icon size={16} aria-hidden />
                </Box>
                <Text component="span" inherit className={styles.navLabel}>
                  {label}
                </Text>
                {highlight && count > 0 ? (
                  <Pill>{count}</Pill>
                ) : (
                  <Text component="span" className={styles.navCount}>
                    {count}
                  </Text>
                )}
              </UnstyledButton>
            );
          })}
        </Box>

        <Box className={styles.results}>
          {sections.map((section) => (
            <Box key={section.key}>
              <Text className={styles.sectionLabel}>{section.label}</Text>
              {section.entries.map((entry) => {
                const n = counts.get(entry.reportType) ?? 0;
                return (
                  <Box key={entry.reportType} className={styles.row}>
                    <Box className={styles.rowText}>
                      <Group gap="xs" wrap="nowrap" miw={0}>
                        <Text component="span" className={styles.rowTitle}>
                          {entry.name}
                        </Text>
                        {entry.isNew && <NewBadge />}
                        {n > 0 && (
                          <Pill tone="tint">
                            {n === 1 ? "In your list" : `In your list ×${n}`}
                          </Pill>
                        )}
                      </Group>
                      <Text className={styles.rowDescription}>
                        {entry.description}
                      </Text>
                    </Box>
                    <Button
                      variant="default"
                      size="xs"
                      className={styles.addButton}
                      loading={addingType === entry.reportType}
                      disabled={!!addingType && addingType !== entry.reportType}
                      aria-label={`${n > 0 ? "Add again" : "Add"}: ${entry.name}`}
                      onClick={() => onAdd(entry)}
                    >
                      {n > 0 ? "Add again" : "Add"}
                    </Button>
                  </Box>
                );
              })}
            </Box>
          ))}
          {sections.length === 0 && (
            <Text className={styles.empty}>
              {matching
                ? `No reports match “${query.trim()}”`
                : "No reports in this category yet."}
            </Text>
          )}
        </Box>
      </Box>
    </>
  );
}
